import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Megaphone,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Announcement {
  id: number;
  agency_id: number;
  title: string;
  body: string | null;
  is_active: number;
  created_by: number | null;
  created_at: string;
  expires_at: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnnouncementsList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingAnn, setEditingAnn] = useState<Announcement | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formExpires, setFormExpires] = useState("");

  const { data: announcements, isLoading } = useQuery<Announcement[]>({
    queryKey: ["announcements"],
    queryFn: async () => {
      const res = await api.get<{ data: Announcement[] }>("/announcements");
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/announcements", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Record<string, unknown>) =>
      api.put(`/announcements/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/announcements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditingAnn(null);
    setFormTitle("");
    setFormBody("");
    setFormExpires("");
  }

  function handleEdit(ann: Announcement) {
    setEditingAnn(ann);
    setFormTitle(ann.title);
    setFormBody(ann.body ?? "");
    setFormExpires(ann.expires_at ?? "");
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data: Record<string, unknown> = {
      title: formTitle,
      body: formBody || undefined,
      expiresAt: formExpires || undefined,
    };

    if (editingAnn) {
      updateMutation.mutate({ id: editingAnn.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  function toggleActive(ann: Announcement) {
    updateMutation.mutate({
      id: ann.id,
      isActive: !ann.is_active,
    });
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Announcements
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage agency-wide announcements
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New Announcement
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingAnn ? "Edit Announcement" : "New Announcement"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Title"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  required
                  placeholder="Announcement title"
                />
                <Input
                  label="Expires At"
                  type="date"
                  value={formExpires}
                  onChange={(e) => setFormExpires(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium leading-none text-foreground">
                  Body
                </label>
                <textarea
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                  placeholder="Announcement content..."
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {(error as Error)?.message ?? "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" loading={isPending}>
                  {editingAnn ? "Save Changes" : "Create Announcement"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && (announcements ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Megaphone className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No announcements yet.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        (announcements ?? []).map((ann) => (
          <Card key={ann.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-foreground">{ann.title}</h3>
                    <button
                      onClick={() => toggleActive(ann)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                        ann.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600",
                      )}
                    >
                      {ann.is_active ? (
                        <>
                          <CheckCircle className="h-3 w-3" /> Active
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" /> Inactive
                        </>
                      )}
                    </button>
                  </div>
                  {ann.body && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {ann.body}
                    </p>
                  )}
                  <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                    <span>
                      Created:{" "}
                      {new Date(ann.created_at).toLocaleDateString()}
                    </span>
                    {ann.expires_at && (
                      <span>
                        Expires:{" "}
                        {new Date(ann.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(ann)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm("Delete this announcement?")) {
                        deleteMutation.mutate(ann.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
